import "./header.css";

import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

import { CONTACT_DETAILS, NAV_LINKS, ROUTES } from "../../data/content";
import { WhatsAppIcon } from "../general/Icons";

const Chevron = () => (
  <svg
    className={"nav-chevron"}
    viewBox={"0 0 12 12"}
    aria-hidden={"true"}
  >
    <path d={"M2.5 4.5L6 8l3.5-3.5"} />
  </svg>
);

//"Rooms" drop-down: opens on hover (desktop) or click / Enter, closes on Escape or clicking elsewhere
const NavDropdown = ({ link, pathname }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const ref = React.useRef(null);
  const isActive = link.children.some((c) => c.path === pathname);

  React.useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const onDown = (e) => {
      if (!ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        ref.current.querySelector("button").focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen]);

  return (
    <li
      ref={ref}
      className={`nav-item has-dropdown ${isOpen ? "is-open" : ""}`}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      onBlur={(e) => {
        if (!ref.current.contains(e.relatedTarget)) {
          setIsOpen(false);
        }
      }}
    >
      <button
        type={"button"}
        className={`nav-link ${isActive ? "is-active" : ""}`}
        aria-expanded={isOpen}
        aria-controls={"rooms-menu"}
        onClick={() => setIsOpen((o) => !o)}
      >
        {link.label}
        <Chevron />
      </button>
      <ul
        id={"rooms-menu"}
        className={"nav-dropdown"}
      >
        {link.children.map((child) => (
          <li key={child.path}>
            <NavLink
              to={child.path}
              className={"nav-dropdown-link"}
            >
              {child.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </li>
  );
};

//Logo on top, then the call link, menu and WhatsApp button (as on Wix). The menu row stays at the top of the
//window while scrolling. Tablets & phones get a slim bar with a full-screen menu
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const { pathname } = useLocation();
  const toggleRef = React.useRef(null);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  React.useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 100);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }
    //Stop the page behind scrolling, and keep keyboard focus inside the menu
    document.body.style.overflow = "hidden";
    const toggle = toggleRef.current;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsMenuOpen(false);
        return;
      }
      if (e.key === "Tab") {
        const focusable = [toggle, ...menuRef.current.querySelectorAll("a, button")];
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
        else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isMenuOpen]);

  return (
    <header className={`site-header ${isScrolled ? "is-scrolled" : ""} ${isMenuOpen ? "is-menu-open" : ""}`}>
      <div className={"header-logo-row"}>
        <Link
          to={ROUTES.HOME}
          className={"site-logo"}
          aria-label={"Graylag & Co - home"}
        >
          <img
            src={"/images/logo.png"}
            alt={""}
            width={600}
            height={121}
          />
        </Link>
      </div>

      <div className={"header-bar"}>
        <Link
          to={ROUTES.HOME}
          className={"site-logo is-compact"}
          aria-label={"Graylag & Co - home"}
        >
          <img
            src={"/images/logo.png"}
            alt={""}
            width={600}
            height={121}
          />
        </Link>

        <a
          href={CONTACT_DETAILS.PHONE.link}
          className={"header-call"}
        >
          <strong>Call:</strong> {CONTACT_DETAILS.PHONE.label}
        </a>

        <nav
          className={"header-nav"}
          aria-label={"Main"}
        >
          <ul className={"nav-list"}>
            {NAV_LINKS.map((link) => (link.children ? (
              <NavDropdown
                key={link.label}
                link={link}
                pathname={pathname}
              />
            ) : (
              <li
                key={link.path}
                className={"nav-item"}
              >
                <NavLink
                  to={link.path}
                  end
                  className={({ isActive }) => `nav-link ${isActive ? "is-active" : ""}`}
                >
                  {link.label}
                </NavLink>
              </li>
            )))}
          </ul>
        </nav>

        <a
          href={CONTACT_DETAILS.WHATSAPP_LINK}
          className={"header-whatsapp"}
          target={"_blank"}
          rel={"noopener noreferrer"}
        >
          Get in Touch on WhatsApp
        </a>

        <button
          ref={toggleRef}
          type={"button"}
          className={"menu-toggle"}
          aria-expanded={isMenuOpen}
          aria-controls={"mobile-menu"}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          onClick={() => setIsMenuOpen((o) => !o)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      <div
        ref={menuRef}
        id={"mobile-menu"}
        className={"mobile-menu"}
        hidden={!isMenuOpen}
      >
        <nav aria-label={"Main"}>
          <ul className={"mobile-menu-list"}>
            {NAV_LINKS.map((link) => (link.children ? (
              <li key={link.label}>
                <span className={"mobile-menu-group"}>{link.label}</span>
                <ul className={"mobile-menu-sublist"}>
                  {link.children.map((child) => (
                    <li key={child.path}>
                      <NavLink
                        to={child.path}
                        className={"mobile-menu-link is-sub"}
                      >
                        {child.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>
            ) : (
              <li key={link.path}>
                <NavLink
                  to={link.path}
                  end
                  className={"mobile-menu-link"}
                >
                  {link.label}
                </NavLink>
              </li>
            )))}
          </ul>
        </nav>
        <div className={"mobile-menu-contact"}>
          <a
            href={CONTACT_DETAILS.PHONE.link}
            className={"header-call"}
          >
            <strong>Call:</strong> {CONTACT_DETAILS.PHONE.label}
          </a>
          <a
            href={CONTACT_DETAILS.WHATSAPP_LINK}
            className={"btn is-whatsapp"}
            target={"_blank"}
            rel={"noopener noreferrer"}
          >
            <WhatsAppIcon />
            Get in Touch on WhatsApp
          </a>
        </div>
      </div>
    </header>
  );
};

export default Header;
